import { useEffect, useMemo, useRef, useState } from "react";

type UseCarouselProps = {
  totalSlides: number;
  slidesPerView: number;
  gap: number;
  step: number;
  draggable: boolean;
};

export function useCustomCarousel({
  totalSlides,
  slidesPerView,
  gap,
  step,
  draggable,
}: UseCarouselProps) {
  const viewportRef = useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  const startX = useRef(0);
  const dragOffset = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    if (!viewportRef.current) return;

    const updateWidth = () => {
      if (!viewportRef.current) return;

      const styles = window.getComputedStyle(viewportRef.current);

      const paddingLeft = parseFloat(styles.paddingLeft);
      const paddingRight = parseFloat(styles.paddingRight);

      const contentWidth =
        viewportRef.current.clientWidth - paddingLeft - paddingRight;

      setViewportWidth(contentWidth);

      const fullSlides = Math.floor(slidesPerView);

      const calculatedWidth =
        (contentWidth - gap * (fullSlides - 1)) / slidesPerView;

      setSlideWidth(calculatedWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => {
        window.removeEventListener("resize", updateWidth);
      };
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(viewportRef.current);

    return () => {
      observer.disconnect();
    };
  }, [gap, slidesPerView]);

  const maxIndex = useMemo(() => Math.max(0, totalSlides - 1), [totalSlides]);

  const next = () => {
    setCurrentIndex((prev) => Math.min(prev + step, maxIndex));
  };

  const prev = () => {
    setCurrentIndex((prev) => Math.max(prev - step, 0));
  };

  const trackWidth = totalSlides * slideWidth + (totalSlides - 1) * gap;

  const maxTranslate = Math.max(0, trackWidth - viewportWidth);

  const translateX = Math.min(currentIndex * (slideWidth + gap), maxTranslate);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable) return;

    startX.current = e.clientX;
    dragOffset.current = 0;
    isDragging.current = true;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggable || !isDragging.current) return;

    dragOffset.current = e.clientX - startX.current;
  };

  const finishDrag = () => {
    if (!draggable || !isDragging.current) return;

    const threshold = slideWidth * 0.2;

    if (dragOffset.current > threshold) {
      prev();
    } else if (dragOffset.current < -threshold) {
      next();
    }

    dragOffset.current = 0;
    isDragging.current = false;
  };

  const onPointerUp = finishDrag;
  const onPointerLeave = finishDrag;

  return {
    viewportRef,
    slideWidth,
    translateX,
    currentIndex,

    next,
    prev,

    canGoPrev: currentIndex > 0,
    canGoNext: translateX < maxTranslate - 1,

    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
  };
}
